import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { api, formatINR, monthLabel } from "../services/mmcapi";
import { useNavigate } from "react-router-dom";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};

const STATUS_LABELS = {
  pending: "Pending",
  overdue: "Overdue",
  paid: "Paid",
  cancelled: "Cancelled"
};

const effectiveStatus = (c) => {
  if (c?.status === "pending" && c?.dueDate && new Date(c.dueDate) < new Date()) {
    return "overdue";
  }
  return c?.status || "pending";
};

const statusBadgeStyle = (s) => {
  if (s === "paid") return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  if (s === "overdue") return { background: "rgba(239,68,68,0.18)", color: "#dc2626" };
  if (s === "cancelled") return { background: "rgba(107,114,128,0.15)", color: "#4b5563" };
  return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
};

const MaintenanceCharges = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState(""); 
  const [status, setStatus] = useState("");
  const [total, setTotal] = useState(0);

  const [showGenerate, setShowGenerate] = useState(false);
  const [genMonth, setGenMonth] = useState(currentMonth());
  const [dueDate, setDueDate] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const navigate = useNavigate();   

  const fetchList = useCallback(async () => {
    try {
      const data = await api("admin.getMMCharges", {
        month: filterMonth || undefined,
        status: status || undefined,
        search: search || undefined,
        page,
        limit
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
    } catch (e) {
      toast.error(e.message);
      setItems([]);
      setTotal(0);
    }
  }, [filterMonth, status, search, page, limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [currentPage, totalPages]);

  const openGenerate = () => {
    setResult(null);
    setShowGenerate(true);
  };

  const closeGenerate = () => {
    if (generating) return;
    setShowGenerate(false);
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!genMonth) {
      toast.error("Select a month");
      return;
    }
    setGenerating(true);
    try {
      const data = await api("admin.generateMMC", {
        month: genMonth,
        dueDate: dueDate || undefined
      });
      setResult(data);
      toast.success(`${data.created} due(s) created`);
      setFilterMonth(genMonth); // show what was just generated
      setPage(1);
      fetchList();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Maintenance Charges</h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Generate and track monthly maintenance charges.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openGenerate}>
            Generate MMC
          </button>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div
                  className="role-table-top d-flex flex-wrap align-items-end justify-content-start"
                  style={{ gap: 12 }}
                >
                  <form
                    onSubmit={applySearch}
                    className="role-table-search-wrap"
                    style={{ flex: "1 1 220px", maxWidth: 360 }}
                  >
                    <div className="form-group mb-0">
                      <label className="form-label" style={{ fontSize: 12 }}>
                        Search
                      </label>
                      <input
                        type="text"
                        className="form-control role-table-search"
                        placeholder="Search name, email, mobile, flat…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </div>
                  </form>

                  <div className="form-group mb-0" style={{ width: 170 }}>
                    <label className="form-label" style={{ fontSize: 12 }}>
                      Month
                    </label>
                    <input
                      type="month"
                      className="form-control"
                      value={filterMonth}
                      onChange={(e) => {
                        setPage(1);
                        setFilterMonth(e.target.value);
                      }}
                    />
                  </div>

                  <div className="form-group mb-0" style={{ width: 160 }}>
                    <label className="form-label" style={{ fontSize: 12 }}>
                      Status
                    </label>
                    <select
                      className="form-control"
                      style={{ width: "100%" }}
                      value={status}
                      onChange={(e) => {
                        setPage(1);
                        setStatus(e.target.value);
                      }}
                    >
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {filterMonth ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setPage(1);
                        setFilterMonth("");
                      }}
                    >
                      All months
                    </button>
                  ) : null}
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}><span className="role-th">Sl No.</span></th>
                        <th><span className="role-th">Month</span></th>
                        <th><span className="role-th">Name</span></th>
                        <th><span className="role-th">Email</span></th>
                        <th style={{ width: 120 }}><span className="role-th">Mobile</span></th>
                        <th style={{ width: 80 }}><span className="role-th">Block</span></th>
                        <th style={{ width: 90 }}><span className="role-th">Flat</span></th>
                        <th style={{ width: 110 }}><span className="role-th">Billed To</span></th>
                        <th style={{ width: 110 }}><span className="role-th">Amount</span></th>
                        <th style={{ width: 110 }}><span className="role-th">Due Date</span></th>
                        <th style={{ width: 120 }}><span className="role-th">Status</span></th>
                        <th style={{ width: 110 }}><span className="role-th">Paid On</span></th>
                        <th style={{ width: 90 }}><span className="role-th">Action</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length ? (
                        items.map((c, idx) => {
                          const st = effectiveStatus(c);
                          return (
                            <tr key={String(c._id || `row-${idx}`)}>
                              <td>{(currentPage - 1) * limit + idx + 1}</td>
                              <td>{monthLabel(c.month)}</td>
                              <td>{c.payerId?.name || "—"}</td>
                              <td>{c.payerId?.email || "—"}</td>
                              <td>{c.payerId?.phone || "—"}</td>
                              <td>{c.block || "—"}</td>
                              <td>{c.flatNumber || "—"}</td>
                              <td>
                                {c.payerRole || "—"}
                                {c.payerRole === "TENANT" && c.ownerId?.name ? (
                                  <div className="text-muted" style={{ fontSize: 11 }}>
                                    Owner: {c.ownerId.name}
                                  </div>
                                ) : null}
                              </td>
                              <td>{formatINR(c.amount)}</td>
                              <td>{formatDate(c.dueDate)}</td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    padding: "0.35rem 0.65rem",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    ...statusBadgeStyle(st)
                                  }}
                                >
                                  {(STATUS_LABELS[st] || st).toUpperCase()}
                                </span>
                              </td>
                              <td>{formatDate(c.paidAt)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="role-action-btn role-action-view"
                                  title="View"
                                  onClick={() =>
                                    navigate(
                                      `/maintenance-charges/view/${c._id}`,
                                    )
                                  }
                                >
                                  <i
                                    className="fa-solid fa-eye"
                                    aria-hidden="true"
                                  />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={12} className="text-center text-muted">
                            {search || filterMonth || status
                              ? "No maintenance charges found"
                              : "No maintenance charges"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  className="role-table-footer d-flex justify-content-between align-items-center flex-wrap"
                  style={{ gap: 10 }}
                >
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <span className="text-muted" style={{ fontSize: 12 }}>Show:</span>
                    <select
                      className="form-control"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(Number(e.target.value));
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Total: {total}
                    </span>
                  </div>

                  <div className="btn-group">
                    <button type="button" className="btn btn-light" disabled={currentPage <= 1} onClick={() => setPage(1)}>
                      &laquo;
                    </button>
                    <button type="button" className="btn btn-light" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      &lsaquo;
                    </button>
                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`btn ${p === currentPage ? "btn-primary" : "btn-light"}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button type="button" className="btn btn-light" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                      &rsaquo;
                    </button>
                    <button type="button" className="btn btn-light" disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)}>
                      &raquo;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showGenerate ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={closeGenerate}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(520px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">Generate Maintenance Charges</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeGenerate}
                disabled={generating}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <form onSubmit={generate}>
              <div className="card-body">
                <div className="row">
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">
                        Month <span className="required-star">*</span>
                      </label>
                      <input
                        type="month"
                        className="form-control"
                        value={genMonth}
                        onChange={(e) => setGenMonth(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Due date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {result ? (
                  <div className="mt-2">
                    <p className="mb-1">
                      Created: <strong>{result.created}</strong> | Already existed:{" "}
                      <strong>{result.alreadyExisted}</strong> | Skipped:{" "}
                      <strong>{(result.skipped || []).length}</strong>
                    </p>
                    {(result.skipped || []).length > 0 ? (
                      <div className="alert alert-warning mb-0">
                        <strong>These flats were not billed:</strong>
                        <ul className="mb-0">
                          {result.skipped.map((s) => (
                            <li key={String(s.ownerId)}>
                              {s.name} ({s.flatNumber || "-"}) - {s.reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeGenerate}
                  disabled={generating}
                >
                  {result ? "Close" : "Cancel"}
                </button>
                <button type="submit" className="btn btn-primary" disabled={generating}>
                  {generating ? "Generating..." : "Generate"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MaintenanceCharges;